import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useQueue } from '../contexts/QueueContext';
import { playlistApi } from '../services/apiService';
import { getAudioUrl, getImageUrl, getR2Url } from '../services/r2Service';
import { Song } from '../components/types';
import { FaPlay, FaPause, FaForward, FaBackward, FaTrash, FaPlus, FaRandom, FaRedo } from 'react-icons/fa';
import { shuffle } from 'lodash';
import styles from '../Vol1.module.css';

const PlaylistView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const { addToQueue, isQueueActive } = useQueue();
  const navigate = useNavigate();

  const [playlist, setPlaylist] = useState<any>(null);
  const [songs, setSongs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [shuffleEnabled, setShuffleEnabled] = useState(false);
  const [shuffledSongs, setShuffledSongs] = useState<any[]>([]);
  const audioRef = useRef<HTMLAudioElement>(new Audio());
  const prevImageUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user) { navigate('/'); return; }
    if (user && id) {
      playlistApi.get(id)
        .then(data => {
          setPlaylist(data.playlist);
          setSongs(data.songs);
          setShuffledSongs(data.songs);
        })
        .catch(() => navigate('/playlists'))
        .finally(() => setIsLoading(false));
    }
  }, [user, authLoading, id, navigate]);

  useEffect(() => {
    if (isQueueActive) { audioRef.current.pause(); setIsPlaying(false); }
  }, [isQueueActive]);

  useEffect(() => {
    const audio = audioRef.current;
    const onMeta = () => setDuration(audio.duration);
    const onTime = () => setCurrentTime(audio.currentTime);
    const onEnd = () => {
      if (repeatEnabled) {
        audio.currentTime = 0;
        audio.play().catch(() => {});
      } else {
        const cs = shuffleEnabled ? shuffledSongs : songs;
        if (currentIndex < cs.length - 1) {
          setCurrentIndex(prev => prev + 1);
        } else {
          setIsPlaying(false);
        }
      }
    };
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
    };
  }, [currentIndex, songs.length]);

  useEffect(() => {
    const cs = shuffleEnabled ? shuffledSongs : songs;
    if (cs.length > 0 && cs[currentIndex]) {
      const song = cs[currentIndex];
      const audioUrl = getAudioUrl(song.audio_path);
      if (audioRef.current.src !== audioUrl) {
        audioRef.current.src = audioUrl;
        if (isPlaying) audioRef.current.play().catch(() => {});
      }
    }
  }, [currentIndex, songs, shuffledSongs, shuffleEnabled, isPlaying]);

  useEffect(() => {
    return () => { audioRef.current.pause(); };
  }, []);

  useEffect(() => {
    const cs = shuffleEnabled ? shuffledSongs : songs;
    const currentSong = cs[currentIndex];
    const coverUrl = currentSong ? getCoverUrl(currentSong.cover_image) : null;
    if (prevImageUrlRef.current !== coverUrl) {
      setIsImageLoading(true);
      prevImageUrlRef.current = coverUrl;
    }
  }, [currentIndex, songs, shuffledSongs, shuffleEnabled]);

  const handleShuffle = () => {
    const cs = songs;
    const currentSong = (shuffleEnabled ? shuffledSongs : cs)[currentIndex];
    const currentPlaybackTime = audioRef.current.currentTime;
    if (shuffleEnabled) {
      const idx = cs.findIndex(s => s.id === currentSong.id);
      setShuffledSongs(cs);
      setCurrentIndex(idx >= 0 ? idx : 0);
    } else {
      const rest = cs.filter(s => s.id !== currentSong.id);
      setShuffledSongs([currentSong, ...shuffle(rest)]);
      setCurrentIndex(0);
    }
    setShuffleEnabled(!shuffleEnabled);
    setRepeatEnabled(false);
    setTimeout(() => { audioRef.current.currentTime = currentPlaybackTime; if (isPlaying) audioRef.current.play().catch(() => {}); }, 0);
  };

  const handleRepeat = () => {
    setRepeatEnabled(!repeatEnabled);
    setShuffleEnabled(false);
  };

  const togglePlay = () => {
    if (isPlaying) { audioRef.current.pause(); }
    else { audioRef.current.play().catch(() => {}); }
    setIsPlaying(!isPlaying);
  };

  const changeSong = (delta: number) => {
    const cs = shuffleEnabled ? shuffledSongs : songs;
    const next = (currentIndex + delta + cs.length) % cs.length;
    setCurrentIndex(next);
  };

  const selectSong = (index: number) => {
    setCurrentIndex(index);
    setIsPlaying(true);
    audioRef.current.play().catch(() => {});
  };

  const removeSong = async (songId: string) => {
    if (!id) return;
    try {
      await playlistApi.removeSong(id, songId);
      setSongs(prev => prev.filter(s => s.id !== songId));
    } catch (err: any) { /* ignore */ }
  };

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = '0' + Math.floor(t % 60);
    return m + ':' + s.substr(-2);
  };

  const getCoverUrl = (coverImage: string) => {
    if (coverImage.startsWith('videos/')) return getR2Url(coverImage);
    return getImageUrl(coverImage);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    audioRef.current.volume = v;
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = parseFloat(e.target.value);
    setCurrentTime(t);
    audioRef.current.currentTime = t;
  };

  if (authLoading || isLoading) {
    return <div className={styles.container}><p>Loading...</p></div>;
  }

  const activeSongs = shuffleEnabled ? shuffledSongs : songs;
  const currentSong = activeSongs[currentIndex];

  return (
    <div className={styles.container}>
      <h1 className={styles.volumeTitle}>{playlist?.name}</h1>
      <p style={{ color: '#815353', marginBottom: '1.5rem' }}>{songs.length} song{songs.length !== 1 ? 's' : ''}</p>

      {currentSong && (
        <>
          <div className={styles.albumCoverContainer}>
            {currentSong.cover_image.match(/\.(mp4|mov|webm)$/i) ? (
              <video
                src={getCoverUrl(currentSong.cover_image)}
                className={`${styles.albumCover} ${isImageLoading ? styles.loading : ''}`}
                autoPlay loop muted playsInline
                onLoadedData={() => setIsImageLoading(false)}
              />
            ) : (
              <img
                src={getCoverUrl(currentSong.cover_image)}
                alt="Cover"
                className={`${styles.albumCover} ${isImageLoading ? styles.loading : ''}`}
                onLoad={() => setIsImageLoading(false)}
              />
            )}
          </div>

          <div className={styles.audioControls}>
            <button onClick={handleShuffle} className={styles.button} title={shuffleEnabled ? 'Disable Shuffle' : 'Enable Shuffle'} disabled={songs.length === 0}>
              <FaRandom className={shuffleEnabled ? styles.activeIcon : styles.icon} />
            </button>
            <button onClick={() => changeSong(-1)} className={styles.button} title="Previous" disabled={songs.length === 0}>
              <FaBackward className={styles.icon} />
            </button>
            <button onClick={togglePlay} className={`${styles.button} ${styles.playButton}`} title={isPlaying ? 'Pause' : 'Play'} disabled={songs.length === 0}>
              {isPlaying ? <FaPause className={styles.icon} /> : <FaPlay className={styles.icon} />}
            </button>
            <button onClick={() => changeSong(1)} className={styles.button} title="Next" disabled={songs.length === 0}>
              <FaForward className={styles.icon} />
            </button>
            <button onClick={handleRepeat} className={styles.button} title={repeatEnabled ? 'Disable Repeat' : 'Enable Repeat'} disabled={songs.length === 0}>
              <FaRedo className={repeatEnabled ? styles.activeIcon : styles.icon} />
            </button>
          </div>

          <div className={styles.playerControls}>
            <div className={styles.trackTitle}>{currentSong.title}</div>
            <div className={styles.timeControls}>
              <div className={styles.timeRow}>
                <span className={styles.timeText}>{formatTime(currentTime)}</span>
                <input type="range" min="0" max={duration} value={currentTime} onChange={handleTimeChange} className={styles.timeSlider} disabled={songs.length === 0} />
                <span className={styles.timeTextRight}>{formatTime(duration)}</span>
              </div>
            </div>
            <div className={styles.volumeSliderWrapper}>
              <input type="range" min="0" max="1" step="0.01" value={volume} onChange={handleVolumeChange} className={styles.volumeSlider} />
            </div>
            <div className={styles.volumeText}>Volume</div>
          </div>
        </>
      )}

      <div className={styles.tracklist}>
        <h2 className={styles.tracklistLabel}>Tracklist</h2>
        {songs.length > 0 ? (
          songs.map((song, i) => (
            <div key={song.id} className={styles.tracklistItem}>
              <span
                onClick={() => selectSong(i)}
                style={{ flex: 1, cursor: 'pointer', color: i === currentIndex ? '#b08a8a' : undefined }}
              >
                {song.title}
              </span>
              <span style={{ color: '#815353', fontSize: '0.8rem', marginRight: '8px' }}>Vol {song.volume}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  addToQueue({
                    id: song.id, title: song.title, volume: song.volume,
                    audioFile: getAudioUrl(song.audio_path), coverImage: getCoverUrl(song.cover_image),
                  } as Song);
                }}
                style={addQueueBtnStyle}
                title="Add to Queue"
              >
                <FaPlus />
              </button>
              <button onClick={() => removeSong(song.id)} style={{ ...addQueueBtnStyle, color: '#ff6b6b' }} title="Remove">
                <FaTrash />
              </button>
            </div>
          ))
        ) : (
          <p className={styles.tracklistLabel}>No songs in this playlist.</p>
        )}
      </div>

      <div className={styles.tapeControls}>
        <button onClick={() => navigate('/playlists')}>Back to Playlists</button>
      </div>
    </div>
  );
};

const addQueueBtnStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: '#815353', cursor: 'pointer',
  padding: '4px 8px', fontSize: '0.8rem', transition: 'color 0.2s',
};

export default PlaylistView;
